// Utilities
import { Document, DocumentStatus, documentDisplayName } from '@/models/Document';
import { Unsubscribe } from 'firebase/auth';
import { doc, getDoc, query, collection, orderBy, updateDoc, getDocs, deleteDoc, serverTimestamp, getCountFromServer, writeBatch } from 'firebase/firestore';
import { defineStore } from 'pinia'
import { useFirestore } from 'vuefire';
import { useAnalyticsStore } from './analytics'
import { useDatasourcesStore } from './datasources'
import { logger } from '@/utils/logger'

export const useDocumentsStore = defineStore('documents', {
  state: () => ({
    isLoading: true,
    documents: [] as Document[],
    // Cache of resolved doc-id → filename for the current project. Cleared
    // implicitly when a different project is queried (the cache key embeds
    // the project id).
    _filenameCache: new Map<string, string | null>() as Map<string, string | null>,
  }),
  actions: {
    listDocuments: async function (pid: string, did?: string, _forceUpdate = false) {
      this.isLoading = true
      if (!pid) {
        this.documents = []
        this.isLoading = false
        return
      }
      const path = Document.collectionPath(pid, did)
      if (!path) {
        this.documents = []
        this.isLoading = false
        return
      }
      const q = query(collection(useFirestore(), path), orderBy("created_at", 'desc'))
      const snapshot = await getDocs(q)
      this.documents = snapshot.docs.map((d) => Document.fromFirebase(d)).filter((d): d is Document => d !== null)
      this.isLoading = false
    },
    updateDocumentStatus: async function (doc: Document, status: string) {
      this.isLoading = true
      await updateDoc(doc.toDocumentReference()!, { updated_at: serverTimestamp(), status: status })
      this.isLoading = false
    },
    deleteDocument: async function (doc: Document) {
      if (!doc?.toDocumentReference()) return
      this.isLoading = true
      await this.updateDocumentStatus(doc, DocumentStatus.TO_DELETE)
      this.isLoading = false
      try { useAnalyticsStore().trackEvent('document_delete') } catch { }
    },
    deleteAllDocuments: async function (pid: string, did?: string) {
      if (!pid) return
      const path = Document.collectionPath(pid, did)
      if (!path) return

      this.isLoading = true
      const firestore = useFirestore()
      const q = query(collection(firestore, path))
      const snapshot = await getDocs(q)
      const docs = snapshot.docs
      const totalDocs = docs.length

      if (totalDocs === 0) {
        this.isLoading = false
        return
      }

      // Use batch operations with smaller batches for more efficient updates
      const BATCH_SIZE = 250 // Firestore has a limit of 500 operations per batch

      for (let i = 0; i < totalDocs; i += BATCH_SIZE) {
        const batch = writeBatch(firestore)
        const batchDocs = docs.slice(i, i + BATCH_SIZE)

        for (const docSnap of batchDocs) {
          batch.update(docSnap.ref, {
            updated_at: serverTimestamp(),
            status: DocumentStatus.TO_DELETE
          })
        }

        await batch.commit()
      }

      this.isLoading = false
      try { useAnalyticsStore().trackEvent('document_delete_all', { count: totalDocs }) } catch { }
    },
    /**
     * One document by its full path: `null` when it no longer exists. A failed
     * read (rules, network) throws after logging, so a caller can tell a
     * document that is gone from one it could not read.
     */
    getDocument: async function (pid: string, dsid: string, docId: string): Promise<Document | null> {
      if (!pid || !dsid || !docId) return null
      try {
        const snap = await getDoc(doc(useFirestore(), `${Document.collectionPath(pid, dsid)}/${docId}`))
        return Document.fromFirebase(snap)
      } catch (e) {
        logger.error('Error reading document', pid, dsid, docId, e)
        throw e
      }
    },
    countDocumentsForDatasource: async function (pid: string, datasourceId: string): Promise<number> {
      if (!pid || !datasourceId) return 0
      const path = Document.collectionPath(pid, datasourceId)
      if (!path) return 0
      const coll = collection(useFirestore(), path)
      const snapshot = await getCountFromServer(coll)
      return snapshot.data().count
    },
    /**
     * Resolve a document's filename by its ID, scoped to a project.
     *
     * Documents live under `projects/{pid}/datasources/{dsid}/documents/{docId}`.
     * We don't know the datasource the document belongs to, so we iterate
     * the project's datasources (loaded by the datasources store) and
     * `getDoc` the candidate path. First hit wins.
     *
     * Avoids a `collectionGroup('documents')` query because that requires a
     * `match /{path=**}/documents/{did}` security rule we don't currently
     * have. The nested per-document rule (`projects/{pid}/datasources/{dsid}/documents/{did}`)
     * covers direct path reads, so this approach works under the existing
     * rules without changes.
     *
     * Cached so repeated lookups (e.g., one per partner row) hit Firestore
     * only the first time per session.
     */
    getDocumentNameById: async function (pid: string, docId: string): Promise<string | null> {
      if (!pid || !docId) return null
      const cacheKey = `${pid}::${docId}`
      if (this._filenameCache.has(cacheKey)) {
        return this._filenameCache.get(cacheKey) ?? null
      }
      try {
        const datasourcesStore = useDatasourcesStore()
        // Ensure datasources for this project are loaded
        await datasourcesStore.listDatasources(pid)
        const db = useFirestore()
        for (const ds of datasourcesStore.datasources) {
          if (!ds.id) continue
          const ref = doc(db, `projects/${pid}/datasources/${ds.id}/documents/${docId}`)
          const snap = await getDoc(ref)
          if (snap.exists()) {
            const found = Document.fromFirebase(snap)
            const name = found ? documentDisplayName(found) || null : null
            this._filenameCache.set(cacheKey, name)
            return name
          }
        }
        this._filenameCache.set(cacheKey, null)
        return null
      } catch (e) {
        // Don't poison the cache on transient errors — caller may retry.
        return null
      }
    },
  }
})
